/** @module csi/action/NewFromController */
define([
	"dojo/_base/declare",
	"designSystem/types/button-types",
	"csi/_base/underscore",
	"csi/action/ActionView",
	"csi/action/MoveFromController",
	"csi/comm",
	"csi/def",
	"csi/locale",
	"csi/reflection",
	"csi/session",
	"csi/xml",
], function(declare, {ButtonEnum}, _, ActionView, MoveFromController, comm, def, locale, reflection, session, xml) {

	return declare(MoveFromController, {
		// Boolean: indicate that user should land on new node after node is created - this restricts to selecting just one node
		bNavigate: false,

		// xml.CallMethod: CopyNode operation for copying node
		m_cnCopy: null,

		// last copied node if any, for example, in the case of "New From" 
		lastCopiedUrl: null,

		// Boolean: indicates new nodes are being created rather than editing existing nodes
		_bNew: true,

		getActionView: function(/*ViewRef?*/vr) {
			// summary: look for view on type of node being created
			if (!this.m_action.m_viewRef) return null;

			// if creating a new node from scratch, then search for view using type of node being created
			var rc = reflection.getClass(this.m_action.m_typeNodeHint);
			var defView = def.getClassViewDef(rc, this.m_action.m_viewRef.m_id);
			if (defView) {
				var url = this.m_urlNode ? this.m_urlNode : this.m_urlParent;
				var nodeContext = this.m_cache.getNode(url);
				return new ActionView(this, defView, nodeContext);
			}

			return this.inherited(arguments);
		},

		getFormData: function() {
			// summary: require at least one selection in popup table view
			let fd = this.m_formData;
			if (!fd) {
				const {_sourcesToCheck, bNavigate, m_relation} = this;

				fd = this.inherited(arguments);

				// if navigating or single relationship, only allow one selection
				if (bNavigate || m_relation?.isSingle) {
					fd.m_bSelectOne = true;
				}

				// if using form for each selected node, then reset for node to null to indicate new node being created
				if (_sourcesToCheck) {
					fd.m_node = null;
				}
			}

			return fd;
		},

		/**
		 * @override
		 * customize the successful message
		 */
		getSuccess: function() {
			const {
				_sourceSelectedLength,
				m_urlNode,
			} = this;

			const newNode = this.getCache().getNode(m_urlNode);
			if (!newNode) {
				return "";
			}

			const isSingle = _sourceSelectedLength == 1;
			const {$Type} = newNode;
			const text = isSingle ? locale.type($Type) : locale.resource("ConjunctionNumberOfType", [this._sourceSelectedLength, locale.typePlural($Type)]);

			return locale.resource("New_Success", [text]);
		},

		addProxyActions: function(/*View*/defView, /*Actions*/defActions) {
			// summary: if can navigate and only one selection, then add Save & Go action as well
			if (!defActions.hasActions()) {
				if (this._canNavigate() && this._sourceSelectedLength == 1) {
					defActions.addAction(this.getProxyAction(ButtonEnum.BUTTON_SAVE_GO, true));
				}

				defActions.addAction(this.getProxyAction(ButtonEnum.BUTTON_SAVE, true, {m_bSubmit: true}));
			}

			this.inherited(arguments);
		},

		getReturnNodeRequest: function(/*String[]*/urlsNew) {
			// summary: add new url to request - return nothing if navigating to new node in which case another view will handle request
			return this.bNavigate ? null : this.inherited(arguments);
		},

		createNewUrl: function() {
			// summary: create URL for new node
			return comm.createUrl();
		},

		createUrlFrom: function(/*Node*/nodeSource) {
			// summary: create URL for given source node
			var type = xml.nodeGetType(nodeSource);

			// if action under relationship, then determine type of new node based on relationship target
			var rc = this.m_action.m_relationship?.getTargetClass();
			if (rc && (rc.getType() != type) && !rc.abstractOrDisabled) {
				type = rc.getType();
			}

			return this.createNewUrl();
		},

		/**
		 * @override
		 * clear the queue so that we enable the underlay and close the dialog
		 */
		_onCancelPopup: function() {
			if (this.isSuspended()) {
				this._sourcesToCheck = null;
				this.closeDialog();
				this.inherited(arguments);
			}
		},

		postProcessSuccess: function() {
			// summary: navigate to new node if so specified - make sure to close popup dialog if in one
			if (this.bNavigate) {
				this.getDialog()?.hide();
				session.setLocationUrl(this.m_urlNode, true, true);
				return null;
			}

			// update the lasted copied url
			this.lastCopiedUrl = this.m_urlNode;

			return this.inherited(arguments);
		},

		/**
		 * Indicate if this action supports navigation to new node (Save & Go)
		 * @return {Boolean} 
		 */
		_canNavigate: function() {
			return def.canNavigateToType(this.m_action.m_typeNodeHint) && !this.widget.bPopup;
		},

		_canRename: function(/*Node*/source) {
			// summary: determine if name should have COPY suffix added
			return !this.m_action.m_request && !this.m_formData && xml.nodeHasAccess(source, xml.RIGHT_WRITE, xml.ATTR_NAME);
		},

		_getCopyName: function(/*Node*/nodeSource, /*String[]*/params) {
			// summary: by default append "-COPY" to name
			return locale.resource("NAME_COPY", params);
		},

		_getFormView: function(/*Node*/node, /*Boolean*/bCheckOnly) {
			// summary: get form view from destination node type rather than source node type
			var a = this.m_action;
			var rc = reflection.getClass(a.m_typeNodeHint) || (a.m_relationship && a.m_relationship.getTargetClass());
			var defView = rc ? def.getClassViewDef(rc, a.idForm, false, bCheckOnly) : null;
			if (this.isValidActionView(defView)) {
				return defView;
			}

			return null;
		},

		_toWriterSource: function() {
			var call = this.toWriter();
			if (!call) {
				this.m_cnCopy = this.m_writer.copyNode(this.m_urlNode, xml.nodeGetURL(this.m_nodeSource));
			}

			// add copy to name if allowed
			if (this._canRename(this.m_nodeSource)) {
				var name = this._getCopyName(this.m_nodeSource, [this.m_nodeSource.$Name]);

				this.m_cnNew = this.m_writer.changeNode(this.m_urlNode).changeAttribute(xml.ATTR_NAME, xml.ATTR_STRING, name);

				// store new name as qualifier if necessary
				if (this.idQualifier == xml.ATTR_NAME) {
					this.qualifier = name;
				}
			}

			return call;
		},

		/**
		 * Flush changes to prevent locking/timeout issue due to uniqueness check in validation rules, and in doing so,
		 * ensure that if there is a validation failure that the copied node(s) are deleted when an error
		 */
		_toWriterValidateNode: function() {
			var w = this.m_writer;

			w.flushChanges();

			_.each(this._getMoveFromUrls(), function(url) {
				w.callMethod("SiteAdmin", "ValidateAndDeleteIfFailed").addParameter(xml.PROP_URL, xml.ATTR_REF, url);
			});
		}

	});
});